/**
 * Meta (Facebook) Pixel client-side integration.
 *
 * Loads the Meta Pixel script and provides typed helpers
 * for tracking standard and custom events.
 *
 * The Pixel ID is read from the VITE_META_PIXEL_ID env var.
 * If the variable is empty the module silently no-ops so the
 * app works fine in development without a real Pixel.
 */

import { isAdSharingSuppressed } from './ad-consent';

/* ---------- fbq type shim ---------- */

type FbqStandardEvent =
  | 'PageView'
  | 'ViewContent'
  | 'Lead'
  | 'Contact'
  | 'CompleteRegistration'
  | 'InitiateCheckout'
  | 'Purchase'
  | 'Search';

interface FbqParams {
  content_name?: string;
  content_category?: string;
  content_ids?: string[];
  content_type?: string;
  value?: number;
  currency?: string;
  status?: string;
  [key: string]: unknown;
}

declare global {
  interface Window {
    fbq?: (
      action: 'init' | 'track' | 'trackCustom',
      eventOrId: string,
      params?: FbqParams,
      options?: { eventID?: string },
    ) => void;
    _fbq?: unknown;
  }
}

/* ---------- state ---------- */

let initialized = false;

const PIXEL_ID = typeof import.meta !== 'undefined'
  ? (import.meta.env?.VITE_META_PIXEL_ID as string | undefined) ?? ''
  : '';

/* ---------- loader ---------- */

/**
 * Inject the Meta Pixel base script into `<head>` and call `fbq('init')`.
 * Safe to call multiple times — subsequent calls are no-ops.
 */
export function initMetaPixel(pixelId?: string): void {
  const id = pixelId || PIXEL_ID;
  if (!id || initialized) return;
  if (isAdSharingSuppressed()) return;

  /* Meta's standard snippet — minified */
  /* eslint-disable */
  (function (f: Window, b: Document, e: string, v: string) {
    if (f.fbq) return;
    const n: any = (f.fbq = function (...args: unknown[]) {
      n.callMethod ? n.callMethod(...args) : n.queue.push(args);
    });
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    const t = b.createElement(e) as HTMLScriptElement;
    t.async = true;
    t.src = v;
    const s = b.getElementsByTagName(e)[0];
    s?.parentNode?.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */

  window.fbq?.('init', id);
  initialized = true;
}

/* ---------- event helpers ---------- */

/** Fire a Meta standard event (PageView, Lead, ViewContent, etc.). Pass `eventId` to
 *  dedupe against a matching server-side CAPI event (Meta merges by event_id). */
export function trackPixelEvent(event: FbqStandardEvent, params?: FbqParams, eventId?: string): void {
  if (!initialized) return;
  // Direct guard, not redundant with initMetaPixel's: covers a visitor who
  // opts out mid-session after `initialized` is already true.
  if (isAdSharingSuppressed()) return;
  if (eventId) window.fbq?.('track', event, params, { eventID: eventId });
  else window.fbq?.('track', event, params);
}

/** Fire a custom (non-standard) event. Pass `eventId` to dedupe against a
 *  matching server-side CAPI event, exactly like `trackPixelEvent`. */
export function trackPixelCustomEvent(event: string, params?: FbqParams, eventId?: string): void {
  if (!initialized) return;
  if (isAdSharingSuppressed()) return;
  if (eventId) window.fbq?.('trackCustom', event, params, { eventID: eventId });
  else window.fbq?.('trackCustom', event, params);
}

/** Convenience: track a PageView. Called once per route. */
export function trackPageView(): void {
  trackPixelEvent('PageView');
}

/** Convenience: track ViewContent with listing metadata. */
export function trackViewContent(listing: {
  id: string;
  name?: string | null;
  industry?: string | null;
  askingPrice?: number | null;
}): void {
  trackPixelEvent('ViewContent', {
    content_ids: [listing.id],
    content_name: listing.name ?? undefined,
    content_category: listing.industry ?? undefined,
    content_type: 'listing',
    value: listing.askingPrice ?? undefined,
    currency: 'USD',
  });
}

/** Convenience: track a Lead conversion (NDA/inquiry form submission). */
export function trackLead(data?: {
  listingId?: string;
  value?: number;
  contentName?: string;
}): void {
  trackPixelEvent('Lead', {
    content_ids: data?.listingId ? [data.listingId] : undefined,
    content_name: data?.contentName ?? undefined,
    value: data?.value ?? undefined,
    currency: 'USD',
  });
}

/** Convenience: track a Contact event (broker inquiry, contact form). */
export function trackContact(data?: {
  contentName?: string;
}): void {
  trackPixelEvent('Contact', {
    content_name: data?.contentName ?? undefined,
  });
}
